import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const parseLocalized = (raw) => {
  if (!raw) return '';
  if (typeof raw === 'object') return raw.en || raw.ru || Object.values(raw)[0] || '';
  try {
    const obj = JSON.parse(raw);
    if (obj && typeof obj === 'object') {
      return obj.en || obj.ru || Object.values(obj)[0] || '';
    }
    return String(raw);
  } catch {
    return String(raw);
  }
};

const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const PRIVACY_LABELS = {
  1: { label: 'Public',  color: '#10b981' },
  2: { label: 'Private', color: '#ef4444' },
  3: { label: 'Hidden',  color: '#6b7280' }
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

const INITIAL_FILTERS = {
  privacy: '',
  dateFrom: '',
  dateTo: '',
  lastActivityFrom: '',
  lastActivityTo: '',
  minMembers: '',
  maxMembers: '',
  minActivities: '',
  maxActivities: '',
  hasActivity: '',
  sortBy: 'created_at',
  sortOrder: 'desc'
};

const Teams = () => {
  const { fetchWithAuth } = useAuth();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [showFilters, setShowFilters] = useState(true);

  const queryString = useMemo(() => {
    const qs = new URLSearchParams();
    if (search) qs.set('search', search);
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== '' && v !== null && v !== undefined) qs.set(k, v);
    });
    return qs.toString();
  }, [search, filters]);

  useEffect(() => {
    const t = setTimeout(fetchTeams, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString]);

  const fetchTeams = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = queryString ? `/api/teams?${queryString}` : '/api/teams';
      const response = await fetchWithAuth(url);
      if (!response.ok) throw new Error(`Failed to fetch teams: ${response.status}`);
      const data = await response.json();
      setTeams(data.teams || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateFilter = (key, value) => setFilters((p) => ({ ...p, [key]: value }));
  const resetFilters = () => {
    setFilters(INITIAL_FILTERS);
    setSearch('');
  };

  const applyDatePreset = (preset) => {
    const today = todayISO();
    if (preset === 'today') {
      updateFilter('dateFrom', today);
      updateFilter('dateTo', today);
    } else if (preset === '7d') {
      setFilters((p) => ({ ...p, dateFrom: daysAgoISO(7), dateTo: today }));
    } else if (preset === '30d') {
      setFilters((p) => ({ ...p, dateFrom: daysAgoISO(30), dateTo: today }));
    } else if (preset === '90d') {
      setFilters((p) => ({ ...p, dateFrom: daysAgoISO(90), dateTo: today }));
    } else if (preset === 'clear') {
      setFilters((p) => ({ ...p, dateFrom: '', dateTo: '' }));
    }
  };

  const applyLastActivityPreset = (preset) => {
    const today = todayISO();
    if (preset === '7d') {
      setFilters((p) => ({ ...p, lastActivityFrom: daysAgoISO(7), lastActivityTo: today }));
    } else if (preset === '30d') {
      setFilters((p) => ({ ...p, lastActivityFrom: daysAgoISO(30), lastActivityTo: today }));
    } else if (preset === 'clear') {
      setFilters((p) => ({ ...p, lastActivityFrom: '', lastActivityTo: '' }));
    }
  };

  const activeFilterCount = Object.entries(filters).filter(
    ([k, v]) => v !== '' && !(k === 'sortBy' && v === 'created_at') && !(k === 'sortOrder' && v === 'desc')
  ).length;

  if (loading && teams.length === 0) return <div className="loading">Loading...</div>;
  if (error) return <div className="error"><h2>Error</h2><p>{error}</p></div>;

  const inputStyle = { padding: '6px 10px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 6, width: '100%' };
  const labelStyle = { fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4, display: 'block' };
  const presetBtn = { padding: '4px 10px', fontSize: 12, border: '1px solid #d1d5db', background: '#fff', borderRadius: 6, cursor: 'pointer' };

  return (
    <div>
      <h1 style={{ fontSize: '32px', fontWeight: '700', marginBottom: '24px' }}>Teams</h1>

      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="search-bar" style={{ marginBottom: showFilters ? 16 : 0 }}>
          <input
            type="text"
            placeholder="Search by name, invite code, owner name or email..."
            className="input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            style={{ ...presetBtn, padding: '8px 14px', fontWeight: 600 }}
          >
            {showFilters ? 'Hide' : 'Show'} filters
            {activeFilterCount > 0 && (
              <span style={{ marginLeft: 6, background: '#3b82f6', color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: 11 }}>
                {activeFilterCount}
              </span>
            )}
          </button>
          <span style={{ color: '#666', fontSize: 14, alignSelf: 'center', whiteSpace: 'nowrap' }}>
            {teams.length} total teams
          </span>
        </div>

        {showFilters && (
          <div style={{ borderTop: '1px solid #eee', paddingTop: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={labelStyle}>Privacy</label>
                <select style={inputStyle} value={filters.privacy} onChange={(e) => updateFilter('privacy', e.target.value)}>
                  <option value="">All</option>
                  <option value="1">Public</option>
                  <option value="2">Private</option>
                  <option value="3">Hidden</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Activity</label>
                <select style={inputStyle} value={filters.hasActivity} onChange={(e) => updateFilter('hasActivity', e.target.value)}>
                  <option value="">All</option>
                  <option value="yes">With activities</option>
                  <option value="no">No activities</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Sort by</label>
                <select style={inputStyle} value={filters.sortBy} onChange={(e) => updateFilter('sortBy', e.target.value)}>
                  <option value="created_at">Created date</option>
                  <option value="name">Name</option>
                  <option value="members">Members</option>
                  <option value="activities">Activities</option>
                  <option value="last_activity">Last activity</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Order</label>
                <select style={inputStyle} value={filters.sortOrder} onChange={(e) => updateFilter('sortOrder', e.target.value)}>
                  <option value="desc">Descending</option>
                  <option value="asc">Ascending</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 12 }}>
              <div>
                <label style={labelStyle}>Created date</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input type="date" style={inputStyle} value={filters.dateFrom} onChange={(e) => updateFilter('dateFrom', e.target.value)} />
                  <input type="date" style={inputStyle} value={filters.dateTo} onChange={(e) => updateFilter('dateTo', e.target.value)} />
                </div>
                <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
                  <button type="button" style={presetBtn} onClick={() => applyDatePreset('today')}>Today</button>
                  <button type="button" style={presetBtn} onClick={() => applyDatePreset('7d')}>7d</button>
                  <button type="button" style={presetBtn} onClick={() => applyDatePreset('30d')}>30d</button>
                  <button type="button" style={presetBtn} onClick={() => applyDatePreset('90d')}>90d</button>
                  <button type="button" style={presetBtn} onClick={() => applyDatePreset('clear')}>Clear</button>
                </div>
              </div>

              <div>
                <label style={labelStyle}>Last activity date</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input type="date" style={inputStyle} value={filters.lastActivityFrom} onChange={(e) => updateFilter('lastActivityFrom', e.target.value)} />
                  <input type="date" style={inputStyle} value={filters.lastActivityTo} onChange={(e) => updateFilter('lastActivityTo', e.target.value)} />
                </div>
                <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
                  <button type="button" style={presetBtn} onClick={() => applyLastActivityPreset('7d')}>Active 7d</button>
                  <button type="button" style={presetBtn} onClick={() => applyLastActivityPreset('30d')}>Active 30d</button>
                  <button type="button" style={presetBtn} onClick={() => applyLastActivityPreset('clear')}>Clear</button>
                </div>
              </div>

              <div>
                <label style={labelStyle}>Members count</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input type="number" min="0" placeholder="Min" style={inputStyle} value={filters.minMembers} onChange={(e) => updateFilter('minMembers', e.target.value)} />
                  <input type="number" min="0" placeholder="Max" style={inputStyle} value={filters.maxMembers} onChange={(e) => updateFilter('maxMembers', e.target.value)} />
                </div>
              </div>

              <div>
                <label style={labelStyle}>Activities count</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input type="number" min="0" placeholder="Min" style={inputStyle} value={filters.minActivities} onChange={(e) => updateFilter('minActivities', e.target.value)} />
                  <input type="number" min="0" placeholder="Max" style={inputStyle} value={filters.maxActivities} onChange={(e) => updateFilter('maxActivities', e.target.value)} />
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" onClick={resetFilters} style={{ ...presetBtn, color: '#ef4444', borderColor: '#fecaca' }}>
                Reset all
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="card">
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Team</th>
                <th>Owner</th>
                <th>Privacy</th>
                <th>Invite Code</th>
                <th style={{ textAlign: 'right' }}>Members</th>
                <th style={{ textAlign: 'right' }}>Activities</th>
                <th>Last Activity</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {teams.map((t) => {
                const name = parseLocalized(t.name) || `Team #${t.id}`;
                const description = parseLocalized(t.description);
                const privacy = PRIVACY_LABELS[t.privacy] || { label: `#${t.privacy}`, color: '#6b7280' };
                return (
                  <tr key={t.id}>
                    <td>{t.id}</td>
                    <td>
                      <Link to={`/teams/${t.id}`} className="link" style={{ fontWeight: 600 }}>
                        {name}
                      </Link>
                      {description && (
                        <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                          {description}
                        </div>
                      )}
                    </td>
                    <td>
                      {t.owner_id ? (
                        <Link to={`/users/${t.owner_id}`} className="link">
                          {t.owner_name || t.owner_email || `User ${t.owner_id}`}
                        </Link>
                      ) : (
                        <span style={{ color: '#999' }}>—</span>
                      )}
                    </td>
                    <td>
                      <span style={{
                        background: privacy.color,
                        color: '#fff',
                        padding: '2px 8px',
                        borderRadius: 12,
                        fontSize: 12,
                        fontWeight: 600
                      }}>
                        {privacy.label}
                      </span>
                    </td>
                    <td><code style={{ fontSize: 12 }}>{t.invite_code}</code></td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{t.member_count}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{t.activity_count}</td>
                    <td style={{ fontSize: 13, color: '#666' }}>{formatDate(t.last_activity_at)}</td>
                    <td style={{ fontSize: 13, color: '#666' }}>{formatDate(t.created_at)}</td>
                    <td>
                      <Link to={`/teams/${t.id}`} className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: 13 }}>
                        View
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {teams.length === 0 && (
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
            No teams found
          </div>
        )}
      </div>
    </div>
  );
};

export default Teams;
